import type { Language } from '@seek/forms-ui/lib/types';

export interface LoginOptions {
  emailAddress: string;
  password: string;
  realm: string;
  flow?: Flow;
  isMobileClient?: boolean;
  history?: any | undefined;
  captchaToken?: string;
}

export interface RegisterOptions {
  newEmailAddress: string;
  newPassword: string;
  connection: string;
  captchaToken?: string;
  language: Language;
  locale: string;
  privacyAccepted?: boolean;
  marketingAccepted?: boolean;
}

export interface ForgotPasswordOptions {
  emailAddress: string;
  connection: string;
}

export interface ChangePasswordOptions {
  emailAddress: string;
  newPassword: string;
  tenant: string;
}

export interface SamlAuthorizeOptions {
  connection: string;
}

export interface PasswordLessOptions {
  emailAddress: string;
  captchaToken?: string;
  code?: string;
  flow?: Flow;
  language?: string;
}

export enum Flow {
  Login = 'login',
  Register = 'register',
  ChangePwd = 'change_pwd',
  Verification = 'verification',
}

export type Auth0LoginErrorCode =
  | 'too_many_attempts'
  | 'unauthorized'
  | 'invalid_user_password'
  | 'duplicate_email_address'
  | 'migration_timeout'
  | 'rate_limited'
  | 'migration_force_password_reset'
  | 'login_not_allowed';

export type Auth0SignupErrorCode =
  | 'user_exists'
  | 'rate_limited'
  | 'pre_registration_error'
  | 'signup_on_sso_domain'
  | 'migration_force_password_reset'
  | 'migration_timeout';

export type Auth0ChangePasswordErrorCode =
  | 'invalid_password'
  | 'password_change_required';

export type RecaptchaErrorCode = 'recaptcha_error';

export type Auth0PasswordLessErrorCode =
  | 'too_many_attempts'
  | 'invalid_user_password'
  | 'rate_limited'
  | 'pre_registration_error'
  | RecaptchaErrorCode;

export type Auth0ChangePasswordErrorName =
  | 'PasswordDictionaryError'
  | 'PasswordStrengthError'
  | 'PasswordNoUserInfoError'
  | 'PasswordHistoryError';

export type RecaptchaNonResetErrorName =
  | 'recaptcha_required'
  | 'error_rendering_recaptcha';

export type PasswordLessClientErrorName =
  | 'code_required'
  | 'code_length_required';

export type PasswordLessServerErrorDesc =
  | 'Wrong email or verification code.'
  | 'The verification code has expired. Please try to login again.'
  | "You've reached the maximum number of attempts. Please try to login again.";

export type FriendlyLoginError =
  | 'Something’s not right. We’ve sent instructions on how to reset your password to your email.'
  | 'Are you sure that’s right? We don’t recognise that combination.'
  | 'Email address is registered to multiple accounts. Please contact customer service.'
  | 'Something went wrong signing you in. Please try again later.'
  | 'Unfortunately, there is a system issue with your account at this time. We are working on it now, please try again later.'
  | 'We have detected too many requests from your IP address. Please try again after ten minutes or contact customer service.'
  | 'Your password has expired and a new one is required to access your account. Please change your password by clicking the ‘Forgot password?’ link above.'
  | 'Sorry, you are temporarily unable to login to JobStreet Philippines with this account.';

export type FriendlyForgotPasswordError = 'Email address is not registered.';

export type FriendlyRecaptchaError =
  | 'Please complete the recaptcha.'
  | 'Error rendering recaptcha. Please refresh the page or contact customer service.';

export type FriendlyChangePasswordError =
  | 'Password is invalid. Please choose a different password.'
  | 'Password is too common. Please choose a different password.'
  | 'Password is too weak. Please choose a different password.'
  | 'Password cannot contain user information. Please choose a different password.'
  | 'Something went wrong changing your password. Please try again later.'
  | 'Password has previously been used. Please choose a different password.'
  | 'Password change required.';

export type FriendlyRegisterError =
  | 'You can’t register with an email already in use. Please sign in or use a different email.'
  | 'We have detected too many requests from your IP address. Please try again after ten minutes or contact customer service.'
  | 'This email is eligible for single sign on. Please use "Sign in with your identity provider" instead.'
  | 'Unfortunately, there is a system issue with your account at this time. We are working on it now, please try again later.';

export type FriendlyPasswordLessClientError =
  | 'Enter your 6 digit code'
  | 'The code should be 6 digits';

export type FriendlyPasswordLessServerError =
  | 'Invalid code. Try again or click resend below.'
  | 'The code has expired.'
  | 'You’ve reached the maximum number of attempts. Please click resend below.'
  | 'Something’s not right. We’ve sent instructions on how to proceed.';

export type Auth0ErrorCode =
  | Auth0LoginErrorCode
  | Auth0SignupErrorCode
  | Auth0ChangePasswordErrorCode
  | RecaptchaErrorCode;

export type FriendlyError =
  | FriendlyLoginError
  | FriendlyChangePasswordError
  | FriendlyRegisterError
  | FriendlyForgotPasswordError
  | FriendlyPasswordLessServerError
  | FriendlyRecaptchaError
  | 'Something went wrong. Please try again later.';
